import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Check, Code2, Smartphone, Users, Brain, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import axios from 'axios';

// Ensure BACKEND_URL is an absolute URL with protocol
const getBackendUrl = () => {
  const url = process.env.REACT_APP_BACKEND_URL;
  if (!url) {
    console.error('REACT_APP_BACKEND_URL is not set');
    return '';
  }
  // If URL doesn't start with http:// or https://, add https://
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
};

const BACKEND_URL = getBackendUrl();
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '';

export const ContactForm = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  const [formData, setFormData] = useState({
    // Step 1
    service: '',
    contactName: '',
    companyName: '',
    contactEmail: '',
    
    // Step 2 - Web Development
    webProjectDescription: '',
    websiteType: '',
    webTimeline: '',
    webBudget: '',
    
    // Step 2 - Mobile Development
    appDescription: '',
    targetPlatform: '',
    appType: '',
    mobileTimeline: '',
    mobileBudget: '',
    
    // Step 2 - IT Staff Augmentation
    rolesNeeded: '',
    numberOfDevelopers: '',
    contractLength: '',
    techStack: '',
    
    // Step 2 - AI Consultancy
    aiUseCase: '',
    aiSolutionType: '',
    projectType: '',
    dataAvailability: ''
  });

  const services = [
    { id: 'web', name: 'Web Development', icon: Code2, color: 'cyan' },
    { id: 'mobile', name: 'Mobile Development', icon: Smartphone, color: 'blue' },
    { id: 'staff', name: 'IT Staff Augmentation', icon: Users, color: 'purple' },
    { id: 'ai', name: 'AI Consultancy', icon: Brain, color: 'green' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateStep = (stepToValidate) => {
    const step = stepToValidate || currentStep;
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.service) {
        newErrors.service = "Please select a service";
      }
      if (!formData.contactName.trim()) {
        newErrors.contactName = "Contact name is required";
      }
      if (!formData.contactEmail.trim()) {
        newErrors.contactEmail = "Email address is required";
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.contactEmail)) {
          newErrors.contactEmail = "Please enter a valid email address";
        }
      }
    }
    
    if (step === 2) {
      if (formData.service === 'web') {
        if (!formData.webProjectDescription.trim()) {
          newErrors.webProjectDescription = "Project description is required";
        }
        if (!formData.websiteType) {
          newErrors.websiteType = "Please select a website type";
        }
      }
      if (formData.service === 'mobile') {
        if (!formData.appDescription.trim()) {
          newErrors.appDescription = "App description is required";
        }
        if (!formData.targetPlatform) {
          newErrors.targetPlatform = "Please select a target platform";
        }
      }
      if (formData.service === 'staff') {
        if (!formData.rolesNeeded) {
          newErrors.rolesNeeded = "Please select the roles needed";
        }
        if (!formData.numberOfDevelopers || parseInt(formData.numberOfDevelopers) < 1) {
          newErrors.numberOfDevelopers = "Number of developers is required";
        }
      }
      if (formData.service === 'ai') {
        if (!formData.aiUseCase.trim()) {
          newErrors.aiUseCase = "AI use case description is required";
        }
        if (!formData.aiSolutionType) {
          newErrors.aiSolutionType = "Please select an AI solution type";
        }
      }
    }
    
    setErrors(newErrors);
    
    if (Object.keys(newErrors).length > 0) {
      toast.error("Please fill in all required fields", {
        description: "Check the form for error messages"
      });
      return false;
    }
    
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => prev + 1);
      setErrors({});
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    setErrors({});
  };

  const handleStepClick = (step) => {
    // Allow clicking only on completed steps or current step
    if (step < currentStep) {
      setCurrentStep(step);
      setErrors({});
    } else if (step === currentStep + 1) {
      // Trying to go to next step - validate current step first
      if (validateStep()) {
        setCurrentStep(step);
        setErrors({});
      }
    }
    // Can't skip ahead beyond next step
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // Validate API URL is configured
      if (!API) {
        toast.error("Configuration Error", {
          description: "Backend URL is not configured. Please contact support."
        });
        setIsSubmitting(false);
        return;
      }

      // Prepare the submission data
      const submissionData = {
        service: formData.service,
        contactName: formData.contactName,
        companyName: formData.companyName || null,
        contactEmail: formData.contactEmail,
        
        // Web Development fields
        webProjectDescription: formData.webProjectDescription || null,
        websiteType: formData.websiteType || null,
        webTimeline: formData.webTimeline || null,
        webBudget: formData.webBudget || null,
        
        // Mobile Development fields
        appDescription: formData.appDescription || null,
        targetPlatform: formData.targetPlatform || null,
        appType: formData.appType || null,
        mobileTimeline: formData.mobileTimeline || null,
        mobileBudget: formData.mobileBudget || null,
        
        // IT Staff Augmentation fields
        rolesNeeded: formData.rolesNeeded || null,
        numberOfDevelopers: formData.numberOfDevelopers || null,
        contractLength: formData.contractLength || null,
        techStack: formData.techStack || null,
        
        // AI Consultancy fields
        aiUseCase: formData.aiUseCase || null,
        aiSolutionType: formData.aiSolutionType || null,
        projectType: formData.projectType || null,
        dataAvailability: formData.dataAvailability || null
      };

      // Submit to backend API
      const response = await axios.post(`${API}/inquiries`, submissionData);
      
      if (response.data) {
        toast.success("Thank you for your inquiry!", {
          description: "We will contact you shortly."
        });
        
        // Reset form
        setFormData({
          service: '', contactName: '', companyName: '', contactEmail: '',
          webProjectDescription: '', websiteType: '', webTimeline: '', webBudget: '',
          appDescription: '', targetPlatform: '', appType: '', mobileTimeline: '', mobileBudget: '',
          rolesNeeded: '', numberOfDevelopers: '', contractLength: '', techStack: '',
          aiUseCase: '', aiSolutionType: '', projectType: '', dataAvailability: ''
        });
        setCurrentStep(1);
        setErrors({});
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error("Submission failed", {
        description: error.response?.data?.detail || "Please try again later or contact us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-8">
      <div>
        <Label className="text-lg font-semibold text-white mb-4 block">
          Choose Service *
        </Label>
        <RadioGroup value={formData.service} onValueChange={(value) => handleInputChange('service', value)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((service) => {
              const Icon = service.icon;
              const isSelected = formData.service === service.id;
              return (
                <label
                  key={service.id}
                  className={`relative flex items-start p-6 rounded-xl border-2 cursor-pointer transition-all ${
                    isSelected
                      ? 'border-cyan-400 bg-cyan-500/10'
                      : errors.service
                      ? 'border-red-500 bg-red-500/5'
                      : 'border-slate-700 bg-slate-900/30 hover:border-slate-600'
                  }`}
                >
                  <RadioGroupItem value={service.id} className="mt-1" />
                  <div className="ml-4 flex-1">
                    <div className="flex items-center gap-3">
                      <Icon className={`w-6 h-6 ${isSelected ? 'text-cyan-400' : 'text-slate-400'}`} />
                      <span className={`font-semibold ${isSelected ? 'text-white' : 'text-slate-300'}`}>
                        {service.name}
                      </span>
                    </div>
                  </div>
                  {isSelected && (
                    <Check className="w-5 h-5 text-cyan-400 absolute top-4 right-4" />
                  )}
                </label>
              );
            })}
          </div>
        </RadioGroup>
        {errors.service && (
          <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{errors.service}</span>
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <Label htmlFor="contactName" className="text-slate-300 mb-2 block">
            Contact Name *
          </Label>
          <Input
            id="contactName"
            value={formData.contactName}
            onChange={(e) => handleInputChange('contactName', e.target.value)}
            placeholder="John Doe"
            className={`bg-slate-900/50 border-slate-700 focus:border-cyan-400 text-white h-12 ${
              errors.contactName ? 'border-red-500 focus:border-red-500' : ''
            }`}
          />
          {errors.contactName && (
            <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.contactName}</span>
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="companyName" className="text-slate-300 mb-2 block">
            Company Name
          </Label>
          <Input
            id="companyName"
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            placeholder="Your Company"
            className="bg-slate-900/50 border-slate-700 focus:border-cyan-400 text-white h-12"
          />
        </div>

        <div>
          <Label htmlFor="contactEmail" className="text-slate-300 mb-2 block">
            Contact Email *
          </Label>
          <Input
            id="contactEmail"
            type="email"
            value={formData.contactEmail}
            onChange={(e) => handleInputChange('contactEmail', e.target.value)}
            placeholder="john@company.com"
            className={`bg-slate-900/50 border-slate-700 focus:border-cyan-400 text-white h-12 ${
              errors.contactEmail ? 'border-red-500 focus:border-red-500' : ''
            }`}
          />
          {errors.contactEmail && (
            <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{errors.contactEmail}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => {
    if (formData.service === 'web') {
      return (
        <div className="space-y-6">
          <div>
            <Label htmlFor="webProjectDescription" className="text-slate-300 mb-2 block">
              Project Description *
            </Label>
            <Textarea
              id="webProjectDescription"
              value={formData.webProjectDescription}
              onChange={(e) => handleInputChange('webProjectDescription', e.target.value)}
              placeholder="Describe your web project..."
              rows={5}
              className={`bg-slate-900/50 border-slate-700 focus:border-cyan-400 text-white resize-none ${
                errors.webProjectDescription ? 'border-red-500 focus:border-red-500' : ''
              }`}
            />
            {errors.webProjectDescription && (
              <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.webProjectDescription}</span>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="websiteType" className="text-slate-300 mb-2 block">
              Type of Website *
            </Label>
            <Select value={formData.websiteType} onValueChange={(value) => handleInputChange('websiteType', value)}>
              <SelectTrigger className={`bg-slate-900/50 border-slate-700 text-white h-12 ${
                errors.websiteType ? 'border-red-500' : ''
              }`}>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="corporate">Corporate</SelectItem>
                <SelectItem value="ecommerce">E-commerce</SelectItem>
                <SelectItem value="saas">SaaS</SelectItem>
                <SelectItem value="internal">Internal Tool</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            {errors.websiteType && (
              <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.websiteType}</span>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="webTimeline" className="text-slate-300 mb-2 block">
              Target Timeline
            </Label>
            <Select value={formData.webTimeline} onValueChange={(value) => handleInputChange('webTimeline', value)}>
              <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white h-12">
                <SelectValue placeholder="Select timeline" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1-2months">1-2 months</SelectItem>
                <SelectItem value="3-4months">3-4 months</SelectItem>
                <SelectItem value="5-6months">5-6 months</SelectItem>
                <SelectItem value="6plus">6+ months</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="webBudget" className="text-slate-300 mb-2 block">
              Estimated Budget Range
            </Label>
            <Select value={formData.webBudget} onValueChange={(value) => handleInputChange('webBudget', value)}>
              <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white h-12">
                <SelectValue placeholder="Select budget range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10k-25k">$10k - $25k</SelectItem>
                <SelectItem value="25k-50k">$25k - $50k</SelectItem>
                <SelectItem value="50k-100k">$50k - $100k</SelectItem>
                <SelectItem value="100k+">$100k+</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    if (formData.service === 'mobile') {
      return (
        <div className="space-y-6">
          <div>
            <Label htmlFor="appDescription" className="text-slate-300 mb-2 block">
              App Description *
            </Label>
            <Textarea
              id="appDescription"
              value={formData.appDescription}
              onChange={(e) => handleInputChange('appDescription', e.target.value)}
              placeholder="Describe your mobile app..."
              rows={5}
              className={`bg-slate-900/50 border-slate-700 focus:border-cyan-400 text-white resize-none ${
                errors.appDescription ? 'border-red-500 focus:border-red-500' : ''
              }`}
            />
            {errors.appDescription && (
              <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.appDescription}</span>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="targetPlatform" className="text-slate-300 mb-2 block">
              Target Platform *
            </Label>
            <Select value={formData.targetPlatform} onValueChange={(value) => handleInputChange('targetPlatform', value)}>
              <SelectTrigger className={`bg-slate-900/50 border-slate-700 text-white h-12 ${
                errors.targetPlatform ? 'border-red-500' : ''
              }`}>
                <SelectValue placeholder="Select platform" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ios">iOS</SelectItem>
                <SelectItem value="android">Android</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
            {errors.targetPlatform && (
              <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.targetPlatform}</span>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="appType" className="text-slate-300 mb-2 block">
              App Type
            </Label>
            <Select value={formData.appType} onValueChange={(value) => handleInputChange('appType', value)}>
              <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white h-12">
                <SelectValue placeholder="Select app type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="consumer">Consumer</SelectItem>
                <SelectItem value="internal">Internal</SelectItem>
                <SelectItem value="mvp">MVP</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="mobileTimeline" className="text-slate-300 mb-2 block">
              Estimated Timeline
            </Label>
            <Select value={formData.mobileTimeline} onValueChange={(value) => handleInputChange('mobileTimeline', value)}>
              <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white h-12">
                <SelectValue placeholder="Select timeline" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2-3months">2-3 months</SelectItem>
                <SelectItem value="4-6months">4-6 months</SelectItem>
                <SelectItem value="6-9months">6-9 months</SelectItem>
                <SelectItem value="9plus">9+ months</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="mobileBudget" className="text-slate-300 mb-2 block">
              Estimated Budget Range
            </Label>
            <Select value={formData.mobileBudget} onValueChange={(value) => handleInputChange('mobileBudget', value)}>
              <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white h-12">
                <SelectValue placeholder="Select budget range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="20k-40k">$20k - $40k</SelectItem>
                <SelectItem value="40k-75k">$40k - $75k</SelectItem>
                <SelectItem value="75k-150k">$75k - $150k</SelectItem>
                <SelectItem value="150k+">$150k+</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    if (formData.service === 'staff') {
      return (
        <div className="space-y-6">
          <div>
            <Label htmlFor="rolesNeeded" className="text-slate-300 mb-2 block">
              Roles Needed *
            </Label>
            <Select value={formData.rolesNeeded} onValueChange={(value) => handleInputChange('rolesNeeded', value)}>
              <SelectTrigger className={`bg-slate-900/50 border-slate-700 text-white h-12 ${
                errors.rolesNeeded ? 'border-red-500' : ''
              }`}>
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="frontend">Frontend</SelectItem>
                <SelectItem value="backend">Backend</SelectItem>
                <SelectItem value="fullstack">Full-stack</SelectItem>
                <SelectItem value="qa">QA</SelectItem>
                <SelectItem value="devops">DevOps</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            {errors.rolesNeeded && (
              <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.rolesNeeded}</span>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="numberOfDevelopers" className="text-slate-300 mb-2 block">
              Number of Developers Needed *
            </Label>
            <Input
              id="numberOfDevelopers"
              type="number"
              min="1"
              value={formData.numberOfDevelopers}
              onChange={(e) => handleInputChange('numberOfDevelopers', e.target.value)}
              placeholder="e.g., 3"
              className={`bg-slate-900/50 border-slate-700 focus:border-cyan-400 text-white h-12 ${
                errors.numberOfDevelopers ? 'border-red-500 focus:border-red-500' : ''
              }`}
            />
            {errors.numberOfDevelopers && (
              <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.numberOfDevelopers}</span>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="contractLength" className="text-slate-300 mb-2 block">
              Expected Contract Length
            </Label>
            <Select value={formData.contractLength} onValueChange={(value) => handleInputChange('contractLength', value)}>
              <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white h-12">
                <SelectValue placeholder="Select duration" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1-3months">1-3 months</SelectItem>
                <SelectItem value="3-6months">3-6 months</SelectItem>
                <SelectItem value="6-12months">6-12 months</SelectItem>
                <SelectItem value="12plus">12+ months</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="techStack" className="text-slate-300 mb-2 block">
              Preferred Tech Stack
            </Label>
            <Input
              id="techStack"
              value={formData.techStack}
              onChange={(e) => handleInputChange('techStack', e.target.value)}
              placeholder="e.g., React, Node.js, Python"
              className="bg-slate-900/50 border-slate-700 focus:border-cyan-400 text-white h-12"
            />
          </div>
        </div>
      );
    }

    if (formData.service === 'ai') {
      return (
        <div className="space-y-6">
          <div>
            <Label htmlFor="aiUseCase" className="text-slate-300 mb-2 block">
              AI Use Case Description *
            </Label>
            <Textarea
              id="aiUseCase"
              value={formData.aiUseCase}
              onChange={(e) => handleInputChange('aiUseCase', e.target.value)}
              placeholder="Describe your AI use case..."
              rows={5}
              className={`bg-slate-900/50 border-slate-700 focus:border-cyan-400 text-white resize-none ${
                errors.aiUseCase ? 'border-red-500 focus:border-red-500' : ''
              }`}
            />
            {errors.aiUseCase && (
              <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.aiUseCase}</span>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="aiSolutionType" className="text-slate-300 mb-2 block">
              Type of AI Solution *
            </Label>
            <Select value={formData.aiSolutionType} onValueChange={(value) => handleInputChange('aiSolutionType', value)}>
              <SelectTrigger className={`bg-slate-900/50 border-slate-700 text-white h-12 ${
                errors.aiSolutionType ? 'border-red-500' : ''
              }`}>
                <SelectValue placeholder="Select solution type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="chatbot">Chatbot</SelectItem>
                <SelectItem value="automation">Automation</SelectItem>
                <SelectItem value="dataanalysis">Data Analysis</SelectItem>
                <SelectItem value="recommendation">Recommendation</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            {errors.aiSolutionType && (
              <div className="flex items-center gap-2 mt-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.aiSolutionType}</span>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="projectType" className="text-slate-300 mb-2 block">
              Project Type
            </Label>
            <Select value={formData.projectType} onValueChange={(value) => handleInputChange('projectType', value)}>
              <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white h-12">
                <SelectValue placeholder="Select project type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="existing">Existing System</SelectItem>
                <SelectItem value="greenfield">Greenfield Project</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="dataAvailability" className="text-slate-300 mb-2 block">
              Data Availability
            </Label>
            <Select value={formData.dataAvailability} onValueChange={(value) => handleInputChange('dataAvailability', value)}>
              <SelectTrigger className="bg-slate-900/50 border-slate-700 text-white h-12">
                <SelectValue placeholder="Select option" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="notsure">Not Sure</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      );
    }

    return null;
  };

  const renderStep3 = () => {
    const selectedService = services.find(s => s.id === formData.service);
    
    return (
      <div className="space-y-6">
        <div className="glass rounded-xl p-6 space-y-4">
          <h3 className="text-xl font-semibold text-white mb-4">Contact Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-slate-400">Service:</span>
              <p className="text-white font-medium">{selectedService?.name}</p>
            </div>
            <div>
              <span className="text-slate-400">Contact Name:</span>
              <p className="text-white font-medium">{formData.contactName}</p>
            </div>
            <div>
              <span className="text-slate-400">Company:</span>
              <p className="text-white font-medium">{formData.companyName || 'N/A'}</p>
            </div>
            <div>
              <span className="text-slate-400">Email:</span>
              <p className="text-white font-medium">{formData.contactEmail}</p>
            </div>
          </div>
        </div>

        <div className="glass rounded-xl p-6 space-y-4">
          <h3 className="text-xl font-semibold text-white mb-4">Project Details</h3>
          <div className="space-y-3 text-sm">
            {formData.service === 'web' && (
              <>
                <div>
                  <span className="text-slate-400">Project Description:</span>
                  <p className="text-white mt-1">{formData.webProjectDescription}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-slate-400">Website Type:</span>
                    <p className="text-white font-medium capitalize">{formData.websiteType}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Timeline:</span>
                    <p className="text-white font-medium">{formData.webTimeline || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Budget:</span>
                    <p className="text-white font-medium">{formData.webBudget || 'N/A'}</p>
                  </div>
                </div>
              </>
            )}

            {formData.service === 'mobile' && (
              <>
                <div>
                  <span className="text-slate-400">App Description:</span>
                  <p className="text-white mt-1">{formData.appDescription}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-slate-400">Platform:</span>
                    <p className="text-white font-medium capitalize">{formData.targetPlatform}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">App Type:</span>
                    <p className="text-white font-medium capitalize">{formData.appType || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Timeline:</span>
                    <p className="text-white font-medium">{formData.mobileTimeline || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Budget:</span>
                    <p className="text-white font-medium">{formData.mobileBudget || 'N/A'}</p>
                  </div>
                </div>
              </>
            )}

            {formData.service === 'staff' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-slate-400">Roles Needed:</span>
                  <p className="text-white font-medium capitalize">{formData.rolesNeeded}</p>
                </div>
                <div>
                  <span className="text-slate-400">Number of Developers:</span>
                  <p className="text-white font-medium">{formData.numberOfDevelopers}</p>
                </div>
                <div>
                  <span className="text-slate-400">Contract Length:</span>
                  <p className="text-white font-medium">{formData.contractLength || 'N/A'}</p>
                </div>
                <div>
                  <span className="text-slate-400">Tech Stack:</span>
                  <p className="text-white font-medium">{formData.techStack || 'N/A'}</p>
                </div>
              </div>
            )}

            {formData.service === 'ai' && (
              <>
                <div>
                  <span className="text-slate-400">Use Case Description:</span>
                  <p className="text-white mt-1">{formData.aiUseCase}</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-slate-400">Solution Type:</span>
                    <p className="text-white font-medium capitalize">{formData.aiSolutionType}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Project Type:</span>
                    <p className="text-white font-medium capitalize">{formData.projectType || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-slate-400">Data Availability:</span>
                    <p className="text-white font-medium capitalize">{formData.dataAvailability || 'N/A'}</p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <p className="text-sm text-slate-400 text-center">
          Review your information and click submit to send your inquiry
        </p>
      </div>
    );
  };

  return (
    <section id="contact" className="py-24 px-6 relative">
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 rounded-full glass mb-4">
            <span className="text-sm text-cyan-400 font-medium">Get In Touch</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            Let's Build Something <span className="gradient-text">Extraordinary</span>
          </h2>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">
            Tell us about your project in 3 simple steps
          </p>
        </div>

        <div className="glass rounded-3xl p-8 md:p-12">
          {/* Progress Indicator */}
          <div className="mb-10">
            <div className="flex items-center justify-center mb-4">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <button
                    type="button"
                    onClick={() => handleStepClick(step)}
                    disabled={step > currentStep + 1}
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
                      step < currentStep
                        ? 'bg-cyan-500 text-white hover:bg-cyan-600 cursor-pointer'
                        : step === currentStep
                        ? 'bg-cyan-500 text-white ring-4 ring-cyan-500/30 cursor-default'
                        : step === currentStep + 1
                        ? 'bg-slate-700 text-slate-400 hover:bg-slate-600 cursor-pointer'
                        : 'bg-slate-700 text-slate-400 cursor-not-allowed'
                    } ${step <= currentStep || step === currentStep + 1 ? '' : 'opacity-50'}`}
                  >
                    {step < currentStep ? <Check className="w-5 h-5" /> : step}
                  </button>
                  {step < 3 && (
                    <div
                      className={`w-24 h-1 mx-2 ${
                        step < currentStep ? 'bg-cyan-500' : 'bg-slate-700'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="text-center">
              <p className="text-slate-400 text-sm">
                Step {currentStep} of 3
              </p>
              <p className="text-white font-semibold mt-1">
                {currentStep === 1 && 'Choose Service'}
                {currentStep === 2 && 'Service Details'}
                {currentStep === 3 && 'Review & Submit'}
              </p>
            </div>
          </div>

          {/* Form Steps */}
          <div className="mb-8">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </div>

          {/* Navigation Buttons */}
          <div className="flex gap-4">
            {currentStep > 1 && (
              <Button
                onClick={handleBack}
                variant="outline"
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800 h-12"
              >
                <ChevronLeft className="w-5 h-5 mr-2" />
                Back
              </Button>
            )}
            
            {currentStep < 3 ? (
              <Button
                onClick={handleNext}
                className="flex-1 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold h-12"
              >
                Next
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-semibold h-12"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                <ChevronRight className="w-5 h-5 ml-2" />
              </Button>
            )}
          </div>
        </div>

        <div className="mt-8 text-center">
          <p className="text-slate-400 text-sm">
            Prefer email? Reach us at{' '}
            <a href="mailto:hello@goappward.com" className="text-cyan-400 hover:text-cyan-300 font-medium">
              hello@goappward.com
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};